// @ts-nocheck
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Grid, TextField, Typography, Divider } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useTranslation } from 'react-i18next';
import { shouldShowIncomingDeliveryReference } from '../../../utils/tenantFeatures';

const CALL_OFF_REGEX = /^[A-Za-z0-9]{10}$/;

const defaultValues = {
  deliveryType: '',
  supplierName: '',
  callOff: '',
  incomingDeliveryReference: '',
  plateNumber: '',
};

const CustomFieldsSection = forwardRef(({
  initialValues = {},
  suppliers = {},
  onChange,
}, ref) => {
  const { t } = useTranslation();
  const shouldRenderIncomingDeliveryReference =
    shouldShowIncomingDeliveryReference();

  const [formData, setFormData] = useState({ ...defaultValues });
  const [errors, setErrors] = useState({ callOff: '' });

  const validateCallOff = (value) => CALL_OFF_REGEX.test((value || '').trim());

  const validate = () => {
    const isValid = validateCallOff(formData.callOff);
    setErrors({
      callOff: isValid ? '' : t('receipts.form.errors.callOffFormat'),
    });
    return isValid;
  };

  const updateFormData = (changes) => {
    const nextData = { ...formData, ...changes };
    setFormData(nextData);
    if (onChange) onChange(nextData);
  };

  // Sync con valores iniciales (edición)
  useEffect(() => {
    const nextData = {
      deliveryType: initialValues.deliveryType || '',
      supplierName: initialValues.supplierName || '',
      callOff: initialValues.callOff || '',
      incomingDeliveryReference: initialValues.incomingDeliveryReference || '',
      plateNumber: initialValues.plateNumber || '',
    };
    setFormData(nextData);
    if (onChange) onChange(nextData);
  }, [
    initialValues.deliveryType,
    initialValues.supplierName,
    initialValues.callOff,
    initialValues.incomingDeliveryReference,
    initialValues.plateNumber,
    onChange,
  ]);

  useEffect(() => {
    if (!formData.callOff) {
      setErrors({ callOff: '' });
      return;
    }

    if (validateCallOff(formData.callOff)) {
      setErrors({ callOff: '' });
    }
  }, [formData.callOff]);

  // Exponer valores al padre
  useImperativeHandle(ref, () => ({
    getValues: () => ({ ...formData }),
    validate,
    reset: () => {
      setFormData({ ...defaultValues });
      setErrors({ callOff: '' });
    },
  }));

  return (
    <Grid item container spacing={2}>
      <Grid item xs={12}>
        <Divider />
        <Typography variant="subtitle1" style={{ marginTop: 10 }}>
          {t('processTypes.receipts_modal.additional_info.title')}
        </Typography>
      </Grid>

      <Grid item xs={12} sm={4}>
        <Autocomplete
          options={['Normal', 'Directa']}
          value={formData.deliveryType || null}
          onChange={(_, val) => updateFormData({ deliveryType: val || '' })}
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={t('processTypes.receipts_modal.additional_info.labels.delivery_type')}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <Autocomplete
          options={Object.values(suppliers)}
          getOptionLabel={(o) => {
            const name = o.entity?.()?.name || o.Entity?.name || '';
            return o.code ? `${name} (${o.code})` : name;
          }}
          value={
            Object.values(suppliers).find(
              (s) => (s.entity?.()?.name || s.Entity?.name) === formData.supplierName,
            ) || null
          }
          onChange={(_, val) =>
            updateFormData({
              supplierName: val?.entity?.()?.name || val?.Entity?.name || '',
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              fullWidth
              label={t('processTypes.receipts_modal.additional_info.labels.supplier_code')}
            />
          )}
        />
      </Grid>

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t('processTypes.receipts_modal.additional_info.labels.call_off')}
          value={formData.callOff}
          onBlur={validate}
          onChange={(e) => updateFormData({ callOff: e.target.value || '' })}
          error={Boolean(errors.callOff)}
          helperText={errors.callOff}
          inputProps={{ maxLength: 10 }}
        />
      </Grid>

      {shouldRenderIncomingDeliveryReference && (
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            label={t('processTypes.receipts_modal.additional_info.labels.incoming_delivery_reference')}
            value={formData.incomingDeliveryReference}
            onChange={(e) => updateFormData({ incomingDeliveryReference: e.target.value || '' })}
          />
        </Grid>
      )}

      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label={t('processTypes.receipts_modal.additional_info.labels.plate_number')}
          value={formData.plateNumber}
          onChange={(e) => {
            const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
            const match = raw.match(/^([A-Z]{0,4})([0-9]{0,4})$/);
            if (match) {
              const letters = match[1].slice(0, 4);
              const numbers = match[2].slice(0, letters.length <= 2 ? 4 : 2);
              updateFormData({ plateNumber: letters + numbers });
            }
          }}
          inputProps={{ maxLength: 6 }}
          placeholder="XX1111 / XXZZ22"
        />
      </Grid>
    </Grid>
  );
});

CustomFieldsSection.displayName = 'CustomFieldsSection';

export default CustomFieldsSection;
